// World: chunk store, block access, solidity, tick state, and streaming lifecycle
// (ENG-3/9/21). Generation is delegated to the pure ChunkGenerator (src/sim/gen)
// so the same terrain is produced synchronously here, in the gen worker, and on
// the server. Chunks are evictable (ENG-21): unedited chunks beyond the keep
// radius are dropped and regenerated on demand; edited chunks are retained
// (persisted to IndexedDB in M1.4).
import { WORLD, LIGHT, FLUIDS } from '../config';
import { NotifyQueue } from './notify';
import type { Registries } from '../registry/load';
import { STATE_AIR, Section, sectionIndexForY, localIndex, DEFAULT_RANGE, type YRange } from './section';
import type { Chunk, PendingTick } from './chunk';
import { Rng, defineSalt } from './rng';
import { ChunkGenerator, FLAT_SURFACE, type GeneratorIds, type GeneratorBiomes, type GeneratorConfig } from '../gen/generator';
import type { IntermediateCity } from '../atlas/intermediate';
import type { AtlasElevation } from '../atlas/elevation';
import type { EarthWorld } from '../earth/earthgen';
import { skyStateFor, type AtlasSolar, type SkyState } from '../atlas/solar';
import { SHAPE, blockBoxes, shapeIdFromName, type Box } from '../shapes';
import { stateEmission } from '../game/glow';
import { CellQueue, SKY_FULL, propagate, unpropagate, lightChunk, type LightGrid } from './light';
import {
  FLUID_NONE, FLUID_WATER, FLUID_LAVA, tickFluid, scheduleAround, flowVector,
  type FluidKind, type FluidWorld,
} from './fluidsim';
import { initialWeather, tickWeather, type WeatherState } from './weather';

/** 6-neighbour offsets for incremental relight. */
const NB6: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1],
];

const S_TICKS = defineSalt('ticks');

const chunkKey = (cx: number, cz: number): string => `${cx},${cz}`;
const floorDiv = (a: number, b: number): number => Math.floor(a / b);
const EMPTY_BOXES: readonly Box[] = [];

// Reusable sky-relight scratch (TAL-3): grown lazily to the tallest range in use
// so a per-edit `relightSkyColumn` allocates nothing (the two `Uint8Array(HEIGHT)`
// per call were the alloc the Earth range magnified 28×). Relight is synchronous +
// non-re-entrant, so a shared pair is safe.
let skyOrigScratch = new Uint8Array(0);
let skyNextScratch = new Uint8Array(0);
function skyRelightScratch(height: number): [Uint8Array, Uint8Array] {
  if (skyOrigScratch.length < height) {
    skyOrigScratch = new Uint8Array(height);
    skyNextScratch = new Uint8Array(height);
  }
  return [skyOrigScratch, skyNextScratch];
}

/**
 * A purely-numeric seed string denotes that number — so `"7"` and `7` are one
 * world (Minecraft semantics). This matters because gen mixes two seeding paths:
 * noise stringifies (`${seed}#salt`, type-independent) while rng features hash
 * via seedFromAny (`hashString("7") !== BigInt(7)`). Without canonicalizing, a
 * seed typed as a string would grow different trees/ores than the same number.
 */
export function canonicalizeSeed(seed: number | string | bigint): number | string | bigint {
  if (typeof seed === 'string' && /^-?\d+$/.test(seed)) {
    const n = Number(seed);
    if (Number.isSafeInteger(n)) return n;
  }
  return seed;
}

/** A block position queued for a scheduled tick (ENG-20 phase 2). */
export interface ScheduledTick {
  x: number;
  y: number;
  z: number;
}

export class World implements FluidWorld {
  private readonly chunks = new Map<string, Chunk>();
  private readonly edited = new Set<string>();
  /** Edited sections keyed `cx,cz,sy` — the save unit (ENG-10). */
  private readonly dirtySections = new Set<string>();
  private readonly solidByState: Uint8Array;
  private readonly shapeByState: Uint8Array;
  private readonly opacityByState: Uint8Array;
  private readonly emissionByState: Uint8Array;
  // ── Fluid classification (ENG-14). Per-state tables the flow algorithm reads. ──
  private readonly fluidKindByState: Uint8Array;
  private readonly fluidLevelByState: Uint8Array;
  private readonly waterloggedByState: Uint8Array;
  private readonly replaceableByFluidState: Uint8Array;
  /** Blocks affected by gravity (sand/gravel/concrete powder — tag `gravity`). */
  private readonly gravityByState: Uint8Array;
  /** Full-cube blocks you sink through — no collision box (powder snow, tag
   *  `passable`). Still opaque + a raycast target, unlike cross/torch/fluid. */
  private readonly passableByState: Uint8Array;
  /** state id for water/lava at each level 0..7 (level 0 = source); -1 if absent. */
  private readonly waterStateByLevel = new Int32Array(8).fill(-1);
  private readonly lavaStateByLevel = new Int32Array(8).fill(-1);
  /** Products of the lava/water reaction (ENG-14). */
  readonly obsidianState: number;
  readonly cobblestoneState: number;
  private readonly rng: Rng;
  private readonly gen: ChunkGenerator;
  /** Water source state id (level 0); -1 if the registry omits water. */
  readonly waterState: number;
  /** Lava source state id (level 0); -1 if the registry omits lava (M7.2). */
  readonly lavaState: number;

  // ── Fluid scheduling (§5.5 phase 4). A dedicated queue, drained in `tickFluids`
  //    — kept separate from block scheduled ticks so fluids stay deterministic. ──
  private readonly fluidScheduled = new Map<number, Array<readonly [number, number, number]>>();
  private readonly fluidPending = new Set<string>();
  /** Chunk keys whose blocks changed during the current `tickFluids`. */
  private fluidDirty = new Set<string>();

  /** Elapsed game ticks (ENG-2). Advanced by the tick pipeline. */
  tickCount = 0;
  /** Time of day in ticks (0..24000). Drives day/night in M3; `/time` sets it. */
  dayTime = 0;
  /** Game rules (`/gamerule`). Persisted with the world in a later milestone. */
  readonly gamerules = new Map<string, string>();
  /** Dedicated deterministic stream for random ticks (ENG-3). */
  readonly tickRng: Rng;
  /** Global weather state (PLR-10); advanced in the §5.5 time/weather phase. */
  readonly weather: WeatherState = initialWeather();
  /** Chunk keys that currently owe scheduled ticks — a drain index so the tick
   *  loop only visits chunks with pending work (ticks themselves live per-chunk). */
  private readonly chunksWithTicks = new Set<string>();
  /** The §27.9 neighbour-notification bus. Every `setBlockState` enqueues its 6 face
   *  neighbours here; the tick pipeline drains it in the `scheduledTicks` phase and
   *  hands the batch to `TickHooks.onNotify`. Kept OUT of `Chunk.scheduledTicks` so
   *  notifications neither persist into saves nor reach `onScheduledTick`'s
   *  redstone/fluid consumers (D-TF-205 — this is what keeps RED-7 byte-identical). */
  readonly notifications = new NotifyQueue();

  /** Flat-world surface Y — re-exported for spawn placement. */
  static readonly FLAT_SURFACE = FLAT_SURFACE;

  /** Canonical world seed (numeric strings normalized to numbers). */
  readonly seed: number | string | bigint;

  /** Which dimension this World simulates (DIM-1). */
  readonly dimension: 'overworld' | 'nether' | 'end';

  /** Real-world solar config (SOL-1) for an Atlas world; null → generic day/night. */
  readonly solar: AtlasSolar | null;

  /** Vertical extent of this dimension (TAL-1). Existing dimensions carry the
   *  default −64..319 range; a tall/Earth world carries a wider one. Every
   *  Y-range read below reads `this.range`, never the global `WORLD.*`. */
  readonly range: YRange;

  constructor(
    readonly registries: Registries,
    seedArg: number | string | bigint = 0,
    opts: { flat?: boolean; dimension?: 'overworld' | 'nether' | 'end'; atlas?: IntermediateCity; atlasElevation?: AtlasElevation; earth?: EarthWorld; solar?: AtlasSolar; flatPreset?: { top: string; filler: string; base: string }; range?: YRange; bounds?: { half: number } } = {},
  ) {
    const seed = canonicalizeSeed(seedArg);
    this.seed = seed;
    this.dimension = opts.dimension ?? 'overworld';
    this.solar = opts.solar ?? null;
    this.range = opts.range ?? DEFAULT_RANGE;
    this.rng = new Rng(seed);
    this.tickRng = this.rng.fork(S_TICKS);

    const { count, blockName } = registries.states;
    this.solidByState = new Uint8Array(count);
    this.shapeByState = new Uint8Array(count);
    this.opacityByState = new Uint8Array(count);
    this.emissionByState = new Uint8Array(count);
    this.fluidKindByState = new Uint8Array(count);
    this.fluidLevelByState = new Uint8Array(count);
    this.waterloggedByState = new Uint8Array(count);
    this.replaceableByFluidState = new Uint8Array(count);
    this.gravityByState = new Uint8Array(count);
    this.passableByState = new Uint8Array(count);
    for (let s = 0; s < count; s++) {
      const def = registries.blocks.byName.get(blockName[s]!)!;
      if (def.tags.includes('gravity')) this.gravityByState[s] = 1;
      if (def.tags.includes('passable')) this.passableByState[s] = 1;
      const shape = shapeIdFromName(def.shape);
      this.shapeByState[s] = shape;
      // Solidity (used by raycast selection): a full cube. Fine-grained collision
      // uses shape boxes via collisionBoxesAt (ENG-18).
      this.solidByState[s] = def.shape === 'cube' && def.material !== 'air' ? 1 : 0;
      // Light attenuation/emission (§5.2). Air is state 0 (opacity 0); the schema
      // defaults are opacity 15 / emission 0.
      this.opacityByState[s] = s === STATE_AIR ? 0 : def.lightOpacity;
      // Fluid classification (ENG-14): the flowing block's kind + `level` property.
      const props = registries.states.props[s]!;
      // Per-state light emission: lit redstone components glow (MB.1).
      this.emissionByState[s] = stateEmission(blockName[s]!, props, def.lightEmission);
      const kind = def.material === 'water' ? FLUID_WATER : def.material === 'lava' ? FLUID_LAVA : FLUID_NONE;
      this.fluidKindByState[s] = kind;
      this.fluidLevelByState[s] = kind !== FLUID_NONE ? Number(props.level ?? '0') : 0;
      this.waterloggedByState[s] = props.waterlogged === 'true' ? 1 : 0;
      // Fluids wash into air and decorative plants (cross/torch); everything else blocks.
      this.replaceableByFluidState[s] = s === STATE_AIR || shape === SHAPE.CROSS || shape === SHAPE.TORCH ? 1 : 0;
    }
    this.waterState = registries.states.defaultOf.get('water') ?? -1;
    this.lavaState = registries.states.defaultOf.get('lava') ?? -1;
    for (let lv = 0; lv <= 7; lv++) {
      this.waterStateByLevel[lv] = registries.states.idByKey.get(`water[level=${lv}]`) ?? -1;
      this.lavaStateByLevel[lv] = registries.states.idByKey.get(`lava[level=${lv}]`) ?? -1;
    }
    this.obsidianState = registries.states.defaultOf.get('obsidian') ?? -1;
    this.cobblestoneState = registries.states.defaultOf.get('cobblestone') ?? -1;

    const req = (key: string): number => {
      const id = registries.states.idByKey.get(key) ?? registries.states.defaultOf.get(key);
      if (id === undefined) throw new Error(`overworld generator needs block '${key}'`);
      return id;
    };
    const ids: GeneratorIds = {
      stone: req('stone'),
      dirt: req('dirt'),
      grass: req('grass_block'),
      log: req('oak_log[axis=y]'),
      leaves: req('oak_leaves'),
    };
    // Flat presets (M24): the flat generator paints top/filler/base from
    // grass/dirt/stone, so a preset is just a remap of those three (flat only).
    if (opts.flat && opts.flatPreset) {
      ids.grass = req(opts.flatPreset.top);
      ids.dirt = req(opts.flatPreset.filler);
      ids.stone = req(opts.flatPreset.base);
    }
    const blockStates: Record<string, number> = {};
    for (const b of registries.blocks.list) {
      blockStates[b.name] = registries.states.defaultOf.get(b.name)!;
      // Also expose the vertical-log state (`name[axis=y]`) the tree generator
      // needs — a block's default axis is 'x', not the upright trunk.
      if (b.properties?.axis) {
        const yId = registries.states.idByKey.get(`${b.name}[axis=y]`);
        if (yId !== undefined) blockStates[`${b.name}[axis=y]`] = yId;
      }
      // Same for the berry-bearing variant lush caves hang (a block's default is
      // the bare vine); without this the generator could only place empty vines.
      if (b.properties?.berries) {
        for (const v of ['0', '1']) {
          const id = registries.states.idByKey.get(`${b.name}[berries=${v}]`);
          if (id !== undefined) blockStates[`${b.name}[berries=${v}]`] = id;
        }
      }
    }
    const biomes: GeneratorBiomes | undefined = registries.biomes
      ? { defs: registries.biomes.defs, placement: registries.biomes.placement, spline: registries.biomes.spline }
      : undefined;

    // Structure placement (M6.3) needs the full state table, in a form that
    // serializes to the gen worker: props per state, states + default per block.
    const s = registries.states;
    const structureStates = {
      props: [...s.props],
      statesOf: Object.fromEntries([...s.statesOf].map(([n, ids]) => [n, [...ids]])),
      defaults: Object.fromEntries(s.defaultOf),
    };

    this.genConfig = {
      seed: typeof seed === 'bigint' ? seed.toString() : seed,
      flat: opts.flat ?? false,
      dimension: this.dimension,
      range: this.range,
      atlas: opts.atlas,
      atlasElevation: opts.atlasElevation,
      earth: opts.earth,
      bounds: opts.bounds,
      ids,
      blockStates,
      biomes,
      ores: registries.features?.ores,
      vegetation: registries.features?.vegetation,
      opacityByState: this.opacityByState,
      emissionByState: this.emissionByState,
      structureStates,
    };
    this.gen = new ChunkGenerator(this.genConfig);
  }

  /** Fired the first time a chunk is created/installed (structure block-entity
   *  ingestion, M6.3). Set by the host; never by the sim. */
  chunkReady?: (cx: number, cz: number) => void;

  /** Biome name at world (x,z) — delegates to the generator (M6.3 structures). */
  biomeNameAt(x: number, z: number): string {
    return this.gen.biomeNameAt(x, z);
  }

  /** Config to initialize a gen worker with the SAME deterministic generator. */
  readonly genConfig: GeneratorConfig;

  isSolidState(state: number): boolean {
    return state !== STATE_AIR && this.solidByState[state] === 1;
  }

  isSolid(x: number, y: number, z: number): boolean {
    return this.isSolidState(this.getBlockState(x, y, z));
  }

  /**
   * Any non-air block a look-ray should select for mining/placing/using: full
   * cubes plus shaped blocks that have a collision box (slabs/stairs/doors). Air,
   * fluids, and boxless decorations (plants/torch) are transparent to targeting,
   * so a door or chest can be right-clicked even when it isn't a full cube (M6).
   */
  isRaycastTarget(x: number, y: number, z: number): boolean {
    const s = this.getBlockState(x, y, z);
    if (s === STATE_AIR) return false;
    if (this.solidByState[s] === 1) return true;
    // Cross-shape plants/crops are selectable (break/harvest/bonemeal) even though
    // they have no collision box (M11.1).
    if (this.shapeByState[s] === SHAPE.CROSS) return true;
    return this.collisionBoxesAt(x, y, z).length > 0;
  }

  /** Local collision boxes (ENG-18) for the block at (x,y,z); [] if passable. */
  collisionBoxesAt(x: number, y: number, z: number): readonly Box[] {
    const s = this.getBlockState(x, y, z);
    if (s === STATE_AIR || this.passableByState[s]) return EMPTY_BOXES;
    return blockBoxes(this.shapeByState[s]!, this.registries.states.props[s]!);
  }

  /** Is the block at (x,y,z) water — any flow level, or a waterlogged block. */
  isFluid(x: number, y: number, z: number): boolean {
    const s = this.getBlockState(x, y, z);
    return this.fluidKindByState[s] === FLUID_WATER || this.waterloggedByState[s] === 1;
  }

  /** Is the block at (x,y,z) lava — any flow level (contact damage / fire, M7.2). */
  isLava(x: number, y: number, z: number): boolean {
    return this.fluidKindByState[this.getBlockState(x, y, z)] === FLUID_LAVA;
  }

  /** Horizontal flow direction of the fluid at (x,y,z), or null if still / dry
   *  (E-10 — pushes loose items and, later, all entities downstream). */
  flowAt(x: number, y: number, z: number): { x: number; z: number } | null {
    if (this.fluidKindByState[this.getBlockState(x, y, z)] === FLUID_NONE) return null;
    const v = flowVector(this, x, y, z);
    return v.x === 0 && v.z === 0 ? null : v;
  }

  // ── Fluid dynamics (ENG-14). World satisfies the FluidWorld interface so the
  //    pure `fluidsim` algorithm drives it directly. ──────────────────────────
  fluidKindOf(state: number): FluidKind {
    return this.fluidKindByState[state] as FluidKind;
  }

  fluidLevelOf(state: number): number {
    return this.fluidLevelByState[state]!;
  }

  isReplaceableByFluid(state: number): boolean {
    return this.replaceableByFluidState[state] === 1;
  }

  isWaterlogged(state: number): boolean {
    return this.waterloggedByState[state] === 1;
  }

  /** Is this a gravity-affected block (sand/gravel/concrete powder, M10.1)? */
  isGravityState(state: number): boolean {
    return this.gravityByState[state] === 1;
  }

  /** Shape id (SHAPE.*) of the block at (x,y,z) — connection resolution (M10.2). */
  shapeAt(x: number, y: number, z: number): number {
    return this.shapeByState[this.getBlockState(x, y, z)]!;
  }

  /** The same block state with its `waterlogged` property set on/off, or -1 if the
   *  block isn't waterloggable (M9.1 waterlogging). Order-independent. */
  waterloggedVariant(state: number, on: boolean): number {
    const key = this.registries.states.key[state];
    if (!key || !key.includes('waterlogged=')) return -1;
    const next = key.replace(/waterlogged=(true|false)/, `waterlogged=${on ? 'true' : 'false'}`);
    return this.registries.states.idByKey.get(next) ?? -1;
  }

  get nether(): boolean {
    return this.dimension === 'nether';
  }

  fluidStateOf(kind: FluidKind, level: number): number {
    if (kind === FLUID_NONE) return STATE_AIR;
    const table = kind === FLUID_WATER ? this.waterStateByLevel : this.lavaStateByLevel;
    return table[Math.max(0, Math.min(7, level))]!;
  }

  /** Commit a fluid-driven block change: set + incrementally relight, tracking
   *  the chunks whose light/geometry changed (drained during `tickFluids`). */
  setFluidBlock(x: number, y: number, z: number, state: number): void {
    const old = this.getBlockState(x, y, z);
    if (old === state) return;
    this.setBlockState(x, y, z, state);
    const dirty = this.relightAfterEdit(x, y, z, old);
    dirty.add(`${floorDiv(x, WORLD.SECTION)},${floorDiv(z, WORLD.SECTION)}`);
    for (const k of dirty) this.fluidDirty.add(k);
  }

  /** Queue a fluid update for a cell after that fluid's flow delay (deduped so a
   *  cell can't be double-queued before it fires). Empty cells use the water
   *  delay; the update is a no-op if nothing flowed in by then. */
  scheduleFluid(x: number, y: number, z: number): void {
    if (y < this.range.minY || y > this.range.maxY) return;
    // Cap the in-flight queue so a pathological source on open terrain can't run
    // away (E-10); natural pooling settles far below this bound.
    if (this.fluidPending.size >= 60_000) return;
    const key = `${x},${y},${z}`;
    if (this.fluidPending.has(key)) return;
    const kind = this.fluidKindByState[this.getBlockState(x, y, z)];
    const delay = kind === FLUID_LAVA ? (this.nether ? FLUIDS.LAVA_FLOW_DELAY_NETHER : FLUIDS.LAVA_FLOW_DELAY) : FLUIDS.WATER_FLOW_DELAY;
    const target = this.tickCount + delay;
    const bucket = this.fluidScheduled.get(target);
    if (bucket) bucket.push([x, y, z]);
    else this.fluidScheduled.set(target, [[x, y, z]]);
    this.fluidPending.add(key);
  }

  /** Schedule the cell plus its 6 neighbours (used when a block edit near a fluid
   *  should let it flow / react). Public entry for the client edit path. */
  scheduleFluidAround(x: number, y: number, z: number): void {
    scheduleAround(this, x, y, z);
  }

  /** Run every fluid update due this tick (§5.5 phase 4). Returns the set of dirty
   *  chunk keys so the client remeshes exactly those. */
  tickFluids(): Set<string> {
    this.fluidDirty = new Set<string>();
    const due = this.fluidScheduled.get(this.tickCount);
    if (due) {
      this.fluidScheduled.delete(this.tickCount);
      for (const [x, y, z] of due) {
        this.fluidPending.delete(`${x},${y},${z}`);
        tickFluid(this, x, y, z);
      }
    }
    return this.fluidDirty;
  }

  // ── Lighting (§5.2). Sky/block channels live in sections; reads outside loaded
  //    data return open-sky / no-block defaults so borders never render black. ──

  opacityOfState(state: number): number {
    return this.opacityByState[state] ?? LIGHT.MAX;
  }

  private sectionAt(x: number, y: number, z: number): Section | null {
    if (y < this.range.minY || y > this.range.maxY) return null;
    const cx = floorDiv(x, WORLD.SECTION), cz = floorDiv(z, WORLD.SECTION);
    return this.getChunk(cx, cz)!.sections[sectionIndexForY(y, this.range)] ?? null;
  }

  getSkyLight(x: number, y: number, z: number): number {
    if (y < this.range.minY) return 0;
    if (y > this.range.maxY) return SKY_FULL;
    const s = this.sectionAt(x, y, z);
    return s ? s.skyLight[localIndex(x, y, z)]! : SKY_FULL; // null section = open sky
  }

  getBlockLight(x: number, y: number, z: number): number {
    const s = this.sectionAt(x, y, z);
    return s ? s.blockLight[localIndex(x, y, z)]! : 0;
  }

  /**
   * A cross-chunk light grid over the chunks around (cx0,cz0). A single relight
   * reaches at most ±1 chunk (light ≤ 15, −1 per step), so a small precomputed
   * neighbourhood makes every cell a plain array index instead of a Map lookup —
   * the difference between a ~5 ms and a sub-ms relight. `channel` 0 = sky
   * (open-sky default 15), 1 = block (default 0).
   */
  private cachedGrid(channel: 0 | 1, cx0: number, cz0: number, mark: (x: number, y: number, z: number) => void): LightGrid {
    const R = 2; // ±2 chunks covers the ±15-block light reach with margin
    const span = 2 * R + 1;
    const near: (Chunk | null)[] = new Array(span * span);
    for (let dz = -R; dz <= R; dz++) {
      for (let dx = -R; dx <= R; dx++) near[(dz + R) * span + (dx + R)] = this.getChunk(cx0 + dx, cz0 + dz)!;
    }
    const chunkOf = (x: number, z: number): Chunk => {
      const cx = x >> 4, cz = z >> 4;
      const ix = cx - cx0 + R, iz = cz - cz0 + R;
      if (ix >= 0 && ix < span && iz >= 0 && iz < span) return near[iz * span + ix]!;
      return this.getChunk(cx, cz)!; // outside the neighbourhood (rare)
    };
    const def = channel === 0 ? SKY_FULL : 0;
    const range = this.range;
    const arr = (s: Section): Uint8Array => (channel === 0 ? s.skyLight : s.blockLight);
    return {
      get: (x, y, z) => {
        if (y < range.minY) return 0;
        if (y > range.maxY) return def;
        const s = chunkOf(x, z).sections[(y >> 4) + range.sectionYOffset];
        return s ? arr(s)[localIndex(x, y, z)]! : def;
      },
      set: (x, y, z, v) => {
        if (y < range.minY || y > range.maxY) return;
        const ch = chunkOf(x, z);
        const si = (y >> 4) + range.sectionYOffset;
        let s = ch.sections[si];
        if (!s) {
          if (v === def) return; // no need to allocate to store the default
          s = new Section(); s.skyLight.fill(SKY_FULL); ch.sections[si] = s;
        }
        arr(s)[localIndex(x, y, z)] = v;
      },
      opacity: (x, y, z) => {
        if (y < range.minY || y > range.maxY) return 0;
        const s = chunkOf(x, z).sections[(y >> 4) + range.sectionYOffset];
        return this.opacityByState[s ? s.getIndex(localIndex(x, y, z)) : STATE_AIR] ?? LIGHT.MAX;
      },
      mark,
    };
  }

  /**
   * Recompute light for a whole chunk from scratch (bulk pass — used after a fill
   * or a load where incremental relight doesn't apply). Self-contained per chunk.
   */
  relightChunk(cx: number, cz: number): void {
    const chunk = this.chunks.get(chunkKey(cx, cz));
    if (chunk) lightChunk(chunk, (s) => this.opacityOfState(s), (s) => this.emissionByState[s] ?? 0);
  }

  /**
   * Incremental relight after a single block edit (LIT-2/3). Returns the set of
   * chunk keys whose light changed, so the caller remeshes exactly those. Assumes
   * the block at (x,y,z) is already the new state. `oldState` (when supplied) lets
   * the sky pass be skipped for transparency-preserving edits (e.g. torches),
   * keeping a single relight well under 2 ms (LIT-4). Chunk marks are packed to
   * integers so the hot BFS never allocates a string per cell.
   */
  relightAfterEdit(x: number, y: number, z: number, oldState?: number): Set<string> {
    const CK = 1 << 15; // chunk-coordinate bias so packed ids stay non-negative
    const dirtyPacked = new Set<number>();
    const mark = (mx: number, _my: number, mz: number): void => {
      dirtyPacked.add(((mx >> 4) + CK) * 65_536 + ((mz >> 4) + CK));
    };
    mark(x, 0, z);

    const newState = this.getBlockState(x, y, z);

    // ── Block light: clear the cell's contribution, then re-seed. ──
    const blk = this.cachedGrid(1, x >> 4, z >> 4, mark);
    const frontier = unpropagate(blk, x, y, z);
    const emission = this.emissionByState[newState] ?? 0;
    if (emission > 0) { blk.set(x, y, z, emission); mark(x, 0, z); frontier.push(x, y, z); }
    for (const [dx, dy, dz] of NB6) {
      if (blk.get(x + dx, y + dy, z + dz) > 0) frontier.push(x + dx, y + dy, z + dz);
    }
    propagate(blk, frontier);

    // ── Sky light: only opacity changes cast/lift shadows — skip when unchanged. ──
    if (oldState === undefined || this.opacityOfState(oldState) !== this.opacityOfState(newState)) {
      this.relightSkyColumn(x, z, mark);
    }

    const dirty = new Set<string>();
    for (const p of dirtyPacked) dirty.add(`${Math.floor(p / 65_536) - CK},${(p % 65_536) - CK}`);
    return dirty;
  }

  /** Top Y of the highest non-null section in column (cx,cz)'s chunk — the sky is a
   *  settled 15 above it (all-air, null sections), so relight never looks there
   *  (TAL-3). Returns `minY − 1` for an entirely-air column. */
  private topFilledY(cx: number, cz: number): number {
    const chunk = this.getChunk(cx, cz)!;
    for (let si = chunk.sections.length - 1; si >= 0; si--) {
      if (chunk.sections[si]) return (si - this.range.sectionYOffset) * WORLD.SECTION + WORLD.SECTION - 1;
    }
    return this.range.minY - 1;
  }

  private relightSkyColumn(x: number, z: number, mark: (x: number, y: number, z: number) => void): void {
    const sky = this.cachedGrid(0, x >> 4, z >> 4, mark);
    const minY = this.range.minY;
    // Bound the reseed to `[minY, topY]`: above the highest filled section every
    // cell is a null-section air cell reading a settled 15 in both the old and the
    // new light, so the loop's work there is pure no-op (TAL-3). Reusable module
    // scratch replaces the two per-call `Uint8Array(HEIGHT)` allocs.
    const topY = this.topFilledY(x >> 4, z >> 4);
    if (topY < minY) return; // all-air column: sky already 15 everywhere (implicit)
    const [orig, next] = skyRelightScratch(this.range.height);

    for (let y = topY; y >= minY; y--) orig[y - minY] = sky.get(x, y, z);
    // Vertical seed (LIT-1): 15 down the column from the top filled section,
    // attenuating by lightOpacity.
    let level: number = SKY_FULL;
    for (let y = topY; y >= minY; y--) {
      const op = sky.opacity(x, y, z);
      if (op > 0) level = Math.max(0, level - op);
      next[y - minY] = level;
    }
    const addQueue = new CellQueue();
    const scratch = [0, 0, 0];
    // Removals first (darker cells cast shadows), then adds (brighter cells spread).
    for (let y = topY; y >= minY; y--) {
      const nw = next[y - minY]!;
      if (nw >= orig[y - minY]!) continue;
      const fr = unpropagate(sky, x, y, z);
      if (nw > 0) { sky.set(x, y, z, nw); mark(x, y, z); addQueue.push(x, y, z); }
      while (fr.shift(scratch)) addQueue.push(scratch[0]!, scratch[1]!, scratch[2]!);
    }
    for (let y = topY; y >= minY; y--) {
      const nw = next[y - minY]!;
      if (nw <= orig[y - minY]!) continue;
      sky.set(x, y, z, nw);
      mark(x, y, z);
      addQueue.push(x, y, z);
    }
    propagate(sky, addQueue);
  }

  hasChunk(cx: number, cz: number): boolean {
    return this.chunks.has(chunkKey(cx, cz));
  }

  get loadedCount(): number {
    return this.chunks.size;
  }

  getChunk(cx: number, cz: number, generate = true): Chunk | undefined {
    const key = chunkKey(cx, cz);
    let chunk = this.chunks.get(key);
    if (!chunk && generate) {
      chunk = this.gen.generate(cx, cz);
      this.chunks.set(key, chunk);
      this.chunkReady?.(cx, cz);
    }
    return chunk;
  }

  /** Install a pre-generated chunk (from the gen worker) unless one already
   *  exists — never clobbers a chunk the player may have edited (ENG-21). */
  installChunk(chunk: Chunk): void {
    const key = chunkKey(chunk.cx, chunk.cz);
    if (!this.chunks.has(key)) {
      this.chunks.set(key, chunk);
      this.chunkReady?.(chunk.cx, chunk.cz);
    }
  }

  /** Drop unedited chunks outside the square keep-radius around (pcx,pcz). Edited
   *  chunks are retained until saved. Returns the number evicted. */
  evictBeyond(pcx: number, pcz: number, radius: number): number {
    let evicted = 0;
    for (const [key, chunk] of this.chunks) {
      if (this.edited.has(key)) continue;
      // Never silently drop a chunk that still owes scheduled ticks — like Java,
      // it stays resident until its ticks fire (or it is saved via dumpScheduledTicks).
      if (chunk.scheduledTicks.length > 0) continue;
      if (Math.abs(chunk.cx - pcx) > radius || Math.abs(chunk.cz - pcz) > radius) {
        this.chunks.delete(key);
        evicted++;
      }
    }
    return evicted;
  }

  isEdited(cx: number, cz: number): boolean {
    return this.edited.has(chunkKey(cx, cz));
  }

  getBlockState(x: number, y: number, z: number): number {
    if (y < this.range.minY || y > this.range.maxY) return STATE_AIR;
    const cx = floorDiv(x, WORLD.SECTION), cz = floorDiv(z, WORLD.SECTION);
    const chunk = this.getChunk(cx, cz)!;
    return chunk.getBlockState(x - cx * WORLD.SECTION, y, z - cz * WORLD.SECTION);
  }

  /** Write a block state and notify its neighbours (§27.9).
   *
   *  `depth` is the cascade depth the resulting notifications carry: 0 for a player or
   *  engine edit, and the dispatching notification's depth + 1 for an edit a handler
   *  made, which is what bounds a chain of reacting blocks (§27.10). */
  setBlockState(x: number, y: number, z: number, state: number, depth = 0): void {
    if (y < this.range.minY || y > this.range.maxY) return;
    const cx = floorDiv(x, WORLD.SECTION), cz = floorDiv(z, WORLD.SECTION);
    const chunk = this.getChunk(cx, cz)!;
    const si = sectionIndexForY(y, this.range);
    const wasNull = !chunk.sections[si];
    chunk.setBlockState(x - cx * WORLD.SECTION, y, z - cz * WORLD.SECTION, state, (s) => this.isSolidState(s));
    // A section newly allocated by this edit was open air → seed its sky to full
    // daylight so incremental relight only has to darken the edited column (E-2).
    if (wasNull && chunk.sections[si]) chunk.sections[si]!.skyLight.fill(SKY_FULL);
    this.edited.add(chunkKey(cx, cz));
    this.dirtySections.add(`${cx},${cz},${si}`);
    // §27.9: tell the 6 face neighbours, but only ENQUEUE — dispatching here would
    // re-enter the edit path mid-mutation. Worldgen never reaches this method (it
    // writes through `Chunk.setBlockState`), so the bus carries game edits only.
    this.notifications.enqueueNeighbors(x, y, z, depth);
  }

  /** Edited sections to persist (ENG-10). `states` is null for a freed section. */
  getDirtySections(): Array<{ cx: number; cz: number; sy: number; states: Uint16Array | null }> {
    const out: Array<{ cx: number; cz: number; sy: number; states: Uint16Array | null }> = [];
    for (const k of this.dirtySections) {
      const [cx, cz, sy] = k.split(',').map(Number) as [number, number, number];
      const chunk = this.chunks.get(chunkKey(cx, cz));
      if (!chunk) continue;
      const sec = chunk.sections[sy];
      out.push({ cx, cz, sy, states: sec ? sec.toStates() : null });
    }
    return out;
  }

  /** Overlay a saved section onto the generated baseline (ENG-10 load). Caller
   *  recomputes the chunk's heightmaps once all its sections are applied. */
  overlaySection(cx: number, cz: number, sy: number, states: Uint16Array | null): void {
    const chunk = this.getChunk(cx, cz)!; // generate baseline first
    chunk.sections[sy] = states ? Section.fromStates(states) : null;
    this.edited.add(chunkKey(cx, cz));
    this.dirtySections.add(`${cx},${cz},${sy}`);
  }

  recomputeChunkHeightmaps(cx: number, cz: number): void {
    this.chunks.get(chunkKey(cx, cz))?.recomputeHeightmaps((s) => this.isSolidState(s));
  }

  loadedChunks(): IterableIterator<Chunk> {
    return this.chunks.values();
  }

  scheduleTick(x: number, y: number, z: number, delay = 1): void {
    const target = this.tickCount + Math.max(1, delay);
    const cx = floorDiv(x, WORLD.SECTION), cz = floorDiv(z, WORLD.SECTION);
    const key = chunkKey(cx, cz);
    this.getChunk(cx, cz)!.scheduledTicks.push({ x, y, z, due: target });
    this.chunksWithTicks.add(key); // cheap drain: only visit chunks that owe ticks
  }

  takeDueTicks(): ScheduledTick[] {
    if (this.chunksWithTicks.size === 0) return [];
    const due: ScheduledTick[] = [];
    for (const key of this.chunksWithTicks) {
      const chunk = this.chunks.get(key);
      if (!chunk || chunk.scheduledTicks.length === 0) { this.chunksWithTicks.delete(key); continue; }
      const keep: PendingTick[] = [];
      // `<=` (not `===`) flushes ticks that came due while the chunk was unloaded
      // and has just been reloaded — matching Java's overdue-tick catch-up.
      for (const t of chunk.scheduledTicks) {
        if (t.due <= this.tickCount) due.push({ x: t.x, y: t.y, z: t.z });
        else keep.push(t);
      }
      chunk.scheduledTicks = keep;
      if (keep.length === 0) this.chunksWithTicks.delete(key);
    }
    return due;
  }

  /** Export a chunk's pending scheduled ticks for persistence (ENG-10 save). */
  dumpScheduledTicks(cx: number, cz: number): PendingTick[] {
    return this.chunks.get(chunkKey(cx, cz))?.scheduledTicks.map((t) => ({ ...t })) ?? [];
  }

  /** Reinstall pending scheduled ticks on a (re)loaded chunk (ENG-10 load). */
  loadScheduledTicks(cx: number, cz: number, ticks: readonly PendingTick[]): void {
    if (ticks.length === 0) return;
    const chunk = this.getChunk(cx, cz)!;
    for (const t of ticks) chunk.scheduledTicks.push({ ...t });
    this.chunksWithTicks.add(chunkKey(cx, cz));
  }

  /** Advance global weather one tick (§5.5 phase 1). Gated by `doWeatherCycle`. */
  advanceWeather(): void {
    tickWeather(this.weather, this.tickRng, this.gamerules.get('doWeatherCycle') !== 'false');
  }

  columnTopState(x: number, z: number): number {
    const cx = floorDiv(x, WORLD.SECTION), cz = floorDiv(z, WORLD.SECTION);
    const chunk = this.getChunk(cx, cz, false);
    if (!chunk) return STATE_AIR;
    const lx = x - cx * WORLD.SECTION, lz = z - cz * WORLD.SECTION;
    const surf = chunk.surfaceY(lx, lz);
    if (surf <= this.range.minY) return STATE_AIR;
    return chunk.getBlockState(lx, surf - 1, lz);
  }

  terrainHeight(x: number, z: number): number {
    return this.gen.terrainHeight(x, z);
  }

  /** The sky the renderer paints this frame (SOL-1, ENG-1 seam). Real sun/moon
   *  position + daylight for an Atlas world (`solar` set); the byte-identical
   *  generic 24 000-tick cycle for every other world. */
  skyState(): SkyState {
    return skyStateFor(this.dayTime, this.solar);
  }

  /** Feet Y of the surface at column (x,z) — the first air cell above the top
   *  block (chunk WORLD_SURFACE heightmap). null if the chunk isn't loaded (mob
   *  spawn placement, MOB-4; never generates a chunk just to probe). */
  surfaceY(x: number, z: number): number | null {
    const cx = floorDiv(x, WORLD.SECTION), cz = floorDiv(z, WORLD.SECTION);
    const chunk = this.getChunk(cx, cz, false);
    if (!chunk) return null;
    const y = chunk.surfaceY(x - cx * WORLD.SECTION, z - cz * WORLD.SECTION);
    return y <= this.range.minY ? null : y;
  }
}
